import { Flex, ButtonV2, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useMemo, useState, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import { FiltersPanelProps } from './FiltersPanel.types';
import { GuestRating } from '../../hooks/useListingFilters';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { debounce } from 'lodash-es';
import { checkboxOverrides, FiltersPanelSkeleton, UserRatingFacet, StarRatingFacet, PriceRangeFacet, FacilitiesFacet } from './components';
import { Close } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { PRICE_RANGE } from '../../constants';

const STAR_RATING_VALUES = [5, 4, 3, 2, 1];

function updateArrayValues<T>(source: T[], newValue: T) {
  return source.includes(newValue) ? source.filter(r => r !== newValue) : [...source, newValue];
}

const Container: React.FC<{}> = ({ children }) => {
  return (
    <Flex flexDirection="column" padding={6} width={{ _: 'full', md: pxToRem(292) }} maxWidth={{ _: 'full', md: pxToRem(292) }} height="100%" overflow="auto" rowGap={6}>
      {children}
    </Flex>
  );
};

const FiltersPanel = ({
  prices,
  onClose,
  onReset,
  filters,
  eventName,
  facilityCounts,
  onFiltersChange,
  isLoadingPrices,
  starRatingCounts,
  guestRatingCounts,
  hidePriceRangeFacet,
  supportedFacilities
}: FiltersPanelProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const filterPanelTrans = t('filterPanel');
  const isMobileOrTablet = useIsMobileOrTablet();
  const [priceRange, setPriceRange] = useState<[number, number]>(filters.priceRange || [PRICE_RANGE.MIN, PRICE_RANGE.MAX]);
  const [debouncedPriceRange, setDebouncedPriceRange] = useState<[number, number]>(filters.priceRange || [PRICE_RANGE.MIN, PRICE_RANGE.MAX]);

  useEffect(() => {
    if (!isEqual(priceRange, filters.priceRange)) {
      setPriceRange(filters.priceRange);
      setDebouncedPriceRange(filters.priceRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.priceRange]);

  const guestRatingsOptions = useMemo(
    () => [
      { rating: 9, text: filterPanelTrans.guestRating.wonderful() },
      { rating: 8, text: filterPanelTrans.guestRating.veryGood() },
      { rating: 7, text: filterPanelTrans.guestRating.good() },
      { rating: 6, text: filterPanelTrans.guestRating.pleasant() }
    ],
    [filterPanelTrans]
  );

  const handleFacilityToggle = useEventCallback((facility: string) => {
    const newFilters = { ...filters, facilities: updateArrayValues(filters.facilities || [], facility) };
    onFiltersChange(newFilters);
  });

  const handleStarRatingToggle = useEventCallback((rating: number): void => {
    const newFilters = { ...filters, starRating: updateArrayValues(filters.starRating || [], rating) };
    onFiltersChange(newFilters);
  });

  const handleUserRatingToggle = useEventCallback((rating: GuestRating): void => {
    const newFilters = { ...filters, userRating: updateArrayValues(filters.userRating || [], rating) };
    onFiltersChange(newFilters);
  });

  const handlePriceRangeChange = useEventCallback((value: number | readonly number[]) => {
    const newRange: [number, number] = Array.isArray(value) ? [value[0], value[1]] : [value, value];
    setPriceRange(newRange);
  });

  useEffect(() => {
    const debouncedUpdate = debounce((value: [number, number]) => {
      setDebouncedPriceRange(value);
    }, 1000);

    debouncedUpdate(priceRange);
    return () => debouncedUpdate.cancel();
  }, [priceRange]);

  useEffect(() => {
    const newFilters = { ...filters, priceRange: debouncedPriceRange };
    onFiltersChange(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPriceRange]);

  return (
    <>
      <Container>
        <Flex justifyContent={'space-between'}>
          {isMobileOrTablet ? (
            <TextV2 typographyVariant="hed4">{filterPanelTrans.filters()}</TextV2>
          ) : (
            <Flex flexDirection="column" gap={2}>
              <TextV2 fontFamily="Forum" fontSize={pxToRem(32)} fontWeight={400}>
                {eventName}
              </TextV2>
              <TextV2 typographyVariant="label2">{filterPanelTrans.findHotels()}</TextV2>
            </Flex>
          )}

          {isMobileOrTablet && <Close onClick={onClose} />}
        </Flex>

        <UserRatingFacet
          title={filterPanelTrans.guestRating.title()}
          guestRatingsOptions={guestRatingsOptions}
          selectedRatings={filters.userRating}
          guestRatingCounts={guestRatingCounts}
          onRatingToggle={handleUserRatingToggle}
          checkboxOverrides={checkboxOverrides}
        />

        {!hidePriceRangeFacet && (
          <PriceRangeFacet
            title={filterPanelTrans.priceRange.title()}
            description={filterPanelTrans.priceRange.description()}
            priceRange={priceRange}
            onPriceRangeChange={handlePriceRangeChange}
            isLoading={isLoadingPrices}
            prices={prices}
          />
        )}

        <StarRatingFacet
          title={filterPanelTrans.starRating.title()}
          description={filterPanelTrans.starRating.description()}
          starRatings={STAR_RATING_VALUES}
          selectedRatings={filters.starRating}
          starRatingCounts={starRatingCounts}
          onRatingToggle={handleStarRatingToggle}
          checkboxOverrides={checkboxOverrides}
        />

        <FacilitiesFacet
          title={filterPanelTrans.facilities.title()}
          facilities={supportedFacilities}
          selectedFacilities={filters.facilities || []}
          facilityCounts={facilityCounts}
          onFacilityToggle={handleFacilityToggle}
          checkboxOverrides={checkboxOverrides}
        />
      </Container>
      {isMobileOrTablet && (
        <Flex gap={4} marginTop={6} width={'100%'} position="sticky" bottom={0} background="white" padding={4}>
          <ButtonV2 variant="ghost" intent="neutral" width="50%" onClick={onReset}>
            {filterPanelTrans.resetAllFilters()}
          </ButtonV2>
          <ButtonV2
            variant="solid"
            intent="neutral"
            shape="rounded"
            width="50%"
            onClick={() => {
              onClose?.();
            }}
          >
            {filterPanelTrans.showResults()}
          </ButtonV2>
        </Flex>
      )}
    </>
  );
};

FiltersPanel.Skeleton = () => {
  return (
    <Container>
      <FiltersPanelSkeleton />
    </Container>
  );
};

export { FiltersPanel };
